import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClientMessage } from '../shared/types';
import type { VoiceListener } from './network';
import { VoiceClient, emptyVoiceSnapshot, type VoiceSnapshot } from './voice';

export interface VoiceApi extends VoiceSnapshot {
  join(withMicrophone?: boolean): Promise<void>;
  leave(): void;
  toggleMute(): Promise<void>;
  resumeAudio(): Promise<void>;
  /** Tear down and rebuild the call after automatic recovery gave up. */
  reconnect(): Promise<void>;
}

export interface VoiceRoom {
  code: string | undefined;
  playerId: string | null;
  /** Read live, not per render: a transport transition is observed before React re-renders. */
  isOnline(): boolean;
  send(message: ClientMessage): void;
  onVoice(listener: VoiceListener): () => void;
}

/**
 * One VoiceClient per seat in one camp. It is rebuilt only when the camp or the courier
 * changes, so a reconnect, a new trail, or a lobby return all keep the same call.
 */
export function useVoiceChat(room: VoiceRoom): VoiceApi {
  const client = useRef<VoiceClient | null>(null);
  const [state, setState] = useState(emptyVoiceSnapshot);
  const { code, playerId } = room;
  // The controller outlives any single render, so it reads the live room through a ref
  // rather than closing over the values this render happened to see.
  const latest = useRef(room);
  latest.current = room;

  useEffect(() => {
    setState(emptyVoiceSnapshot);
    if (!code || !playerId) return;
    const voice = new VoiceClient({
      send: (message) => latest.current.send(message),
      subscribe: (listener) => latest.current.onVoice(listener),
      online: () => latest.current.isOnline(),
    }, { code, playerId }, setState);
    client.current = voice;
    voice.start();
    return () => { client.current = null; voice.dispose(); };
  }, [code, playerId]);

  const join = useCallback(async (withMicrophone = true) => { await client.current?.join(withMicrophone); }, []);
  const leave = useCallback(() => { client.current?.leave(); }, []);
  const toggleMute = useCallback(async () => { await client.current?.toggleMute(); }, []);
  const resumeAudio = useCallback(async () => { await client.current?.resumeAudio(); }, []);
  const reconnect = useCallback(async () => { await client.current?.reconnect(); }, []);
  return { ...state, join, leave, toggleMute, resumeAudio, reconnect };
}
